// Resolves quantity-tier overrides. Pure - no imports, no Vue, no DOM, so Node's
// built-in test runner executes it directly.
//
// Mirrored by server/src/ProdFlop.Server.Services/QuantityTierResolver.cs. Both
// implementations are asserted against Docs/fixtures/quantity-tiers.json; if you
// change behaviour here, change it there and add a fixture vector covering it.
//
// Tier documents are keyed by a threshold written as a decimal integer string. The
// applicable tier is the highest threshold less than or equal to the quantity.
// Object key order is never relied upon - keys are parsed and compared as integers.

// A tier key is valid only if it is digits only - no sign, no leading or trailing
// whitespace, no decimal point, no exponent - and fits in a 32-bit signed integer
// (0..2147483647). This must match the C# resolver's
// int.TryParse(key, NumberStyles.None, CultureInfo.InvariantCulture) exactly,
// including its Int32 bound: NumberStyles.None already rejects whitespace and
// signs, and int.TryParse fails (rather than throwing) on overflow, so a key like
// "99999999999" is silently ignored on the C# side. Without the explicit bound
// check below, Number(key) would happily parse that same key into a valid (if
// oversized) JS number, and the two resolvers would disagree about whether the
// key is a real threshold - exactly the kind of divergence the shared fixture
// exists to catch. Do not trim() before testing: NumberStyles.None does not
// tolerate whitespace, so neither must this regex.
const INT32_MAX = 2147483647

function parseThreshold(key) {
  if (!/^\d+$/.test(String(key))) {
    return null
  }
  const value = Number(key)
  return value > INT32_MAX ? null : value
}

/**
 * The highest threshold less than or equal to `quantity`, or null when no tier
 * applies: a null/undefined/empty document, a quantity below every threshold, or a
 * document whose keys are all unparseable.
 */
export function resolveThreshold(quantity, tiers) {
  const key = resolveKey(quantity, tiers)
  return key === null ? null : Number(key)
}

/** The applicable tier entry, or null when no tier applies. */
export function resolveTier(quantity, tiers) {
  const key = resolveKey(quantity, tiers)
  return key === null ? null : tiers[key]
}

// Returns the winning document key verbatim, so lookups use the key the document
// actually holds. Re-deriving it from the parsed number would break on a key whose
// own spelling differs from its canonical re-stringified form - '0100' parses to
// 100, but tiers['100'] would be undefined while tiers['0100'] holds the entry.
function resolveKey(quantity, tiers) {
  if (!tiers) {
    return null
  }

  let bestKey = null
  let best = null

  for (const key of Object.keys(tiers)) {
    const threshold = parseThreshold(key)
    if (threshold === null) {
      continue
    }
    if (threshold <= quantity && (best === null || threshold > best)) {
      best = threshold
      bestKey = key
    }
  }

  return bestKey
}

/**
 * The company coefficient in force at `quantity`, falling back to
 * `baseCoeficient` when no tier applies.
 */
export function effectiveCompanyCoeficient(baseCoeficient, tiers, quantity) {
  const tier = resolveTier(quantity, tiers)
  return tier ? tier.companyCoeficient : baseCoeficient
}

/**
 * The durations in force at `quantity`, falling back to the base pair when no tier
 * applies. Tier entries are dense, so a resolved tier supplies both values and
 * neither is composed field-by-field.
 */
export function effectiveDurations(baseDuration, baseRealDuration, tiers, quantity) {
  const tier = resolveTier(quantity, tiers)

  return tier
    ? { durationInMinutes: tier.durationInMinutes, realDurationInMinutes: tier.realDurationInMinutes }
    : { durationInMinutes: baseDuration, realDurationInMinutes: baseRealDuration }
}
